/**
 * Changes tab of the editor (Paper "Experiments · 02b Editor · Changes tab"):
 * every change on the version being edited, newest first, with Edit (select
 * it on the page) and Revert, and Revert all at the foot.
 */
import { useState } from "react"
import { instructionTextValue } from "@humblytics/editor-core"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { ComponentInstruction, InstructionOperation } from "./utils/instructionTypes"

const KIND: Partial<Record<InstructionOperation, { label: string; glyph: string }>> = {
  text: { label: "Text", glyph: "T" },
  css: { label: "Style", glyph: "◐" },
  image: { label: "Image", glyph: "▣" },
  visibility: { label: "Visibility", glyph: "◌" },
  reorder: { label: "Order", glyph: "⇅" },
  html: { label: "HTML", glyph: "<>" },
  attribute: { label: "Attribute", glyph: "@" },
  form_field: { label: "Form field", glyph: "▭" },
  delete: { label: "Removed", glyph: "✕" },
}

function summary(instruction: ComponentInstruction): string {
  switch (instruction.operation) {
    case "text":
      return instructionTextValue(instruction.value) ?? ""
    case "css": {
      const value = instruction.value as { properties?: Record<string, string> } & Record<string, unknown>
      const props = value?.properties ?? value ?? {}
      return Object.entries(props)
        .filter(([, v]) => typeof v === "string")
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" · ")
    }
    case "visibility":
      return (instruction.value as { visible?: boolean })?.visible ? "Shown" : "Hidden"
    case "image":
      return "New image"
    default:
      return ""
  }
}

type EditorChangesPanelProps = {
  versionLabel: string
  instructions: ComponentInstruction[]
  onEdit: (instruction: ComponentInstruction) => void
  onRemove: (instructionId: string) => void
  onClearAll: () => void
}

export function EditorChangesPanel({ versionLabel, instructions, onEdit, onRemove, onClearAll }: EditorChangesPanelProps) {
  const [confirmClear, setConfirmClear] = useState(false)
  const newestFirst = [...instructions].reverse()

  return (
    <div className="flex min-h-0 grow flex-col">
      <div className="min-h-0 grow overflow-y-auto">
        <div className="flex flex-col gap-1 px-5 pb-3 pt-5">
          <h2 className="text-base font-semibold leading-5 text-ui-title">Changes in {versionLabel}</h2>
          <p className="text-[13px] leading-[19px] text-foreground/60">
            Everything else on the page stays the same as Control.
          </p>
        </div>
        {instructions.length === 0 ? (
          <p className="px-5 py-6 text-[13px] leading-[19px] text-foreground/60">
            No changes yet. Click any element on the page to change it.
          </p>
        ) : (
          <div className="flex flex-col gap-2 px-5 pb-5">
            {newestFirst.map((instruction) => {
              const kind = KIND[instruction.operation] ?? { label: "Change", glyph: "•" }
              const text = summary(instruction)
              return (
                <div key={instruction.id} className="flex flex-col gap-2.5 rounded-xl p-3 ring-1 ring-ui-hairline">
                  <div className="flex items-start gap-2.5">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-xs font-semibold text-ui-title ring-1 ring-ui-hairline">
                      {kind.glyph}
                    </span>
                    <div className="flex min-w-0 grow flex-col">
                      <span className="text-sm font-semibold leading-[18px] text-ui-title">{kind.label}</span>
                      <span className="truncate font-mono text-xs leading-4 text-foreground/60" title={instruction.selector}>
                        {instruction.selector}
                      </span>
                    </div>
                  </div>
                  {text && (
                    <p className="line-clamp-3 rounded-lg bg-muted px-3 py-2 text-[13px] leading-[18px] text-ui-title">{text}</p>
                  )}
                  <div className="flex gap-2">
                    <SmallButton onClick={() => onEdit(instruction)}>Edit</SmallButton>
                    <SmallButton onClick={() => onRemove(instruction.id)}>Revert</SmallButton>
                  </div>
                </div>
              )
            })}
            {instructions.length > 1 && (
              <p className="rounded-lg bg-muted px-3 py-2.5 text-xs leading-[18px] text-foreground/60">
                Changes in one version are tested together. The result tells you {versionLabel.replace("Variant ", "")} won, not which change did it.
              </p>
            )}
          </div>
        )}
      </div>
      {instructions.length > 0 && (
        <div className="flex items-center border-t border-border px-5 pb-[18px] pt-4">
          <button type="button" onClick={() => setConfirmClear(true)} className="text-[13px] leading-4 text-foreground/60 hover:text-foreground">
            Revert all
          </button>
        </div>
      )}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert every change?</AlertDialogTitle>
            <AlertDialogDescription>
              {versionLabel} goes back to the page as it is today. You can't undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep changes</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setConfirmClear(false)
                onClearAll()
              }}
            >
              Revert all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function SmallButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-7 rounded-lg px-2.5 text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] transition-colors hover:bg-muted dark:ring-white/10"
    >
      {children}
    </button>
  )
}

export default EditorChangesPanel
